import * as T from 'three';
import type { RideTrack } from '../model';
import type { Terrain } from './terrain';
import { makeNoise } from './noise';

interface GrassStyle { density: number; height: [number, number]; colors: string[]; tip: string }
const STYLES: Record<RideTrack['biome'], GrassStyle> = {
  alpine: { density: 1.5, height: [.32, .62], colors: ['#4f6a2d', '#5d7633', '#66793a', '#4a5f2c'], tip: '#a4ad6a' },
  meadow: { density: 2.1, height: [.38, .82], colors: ['#5d7f2e', '#6c8b34', '#78923b', '#88964a'], tip: '#c7c47a' },
  park: { density: 1.4, height: [.3, .52], colors: ['#5e7a32', '#6a8238', '#73883f'], tip: '#b7b979' },
  coast: { density: .9, height: [.28, .6], colors: ['#8d8648', '#9b9150', '#7c7f45', '#a69a5d'], tip: '#d8c88c' },
  harbour: { density: .75, height: [.24, .46], colors: ['#62783f', '#6e8146', '#7a8a4d'], tip: '#b9bb84' },
  canyon: { density: .28, height: [.3, .55], colors: ['#a08a55', '#8f7e4e', '#b19a63'], tip: '#ddc58f' },
};

function tuftGeometry(seed: number) {
  const n = makeNoise(seed), pos: number[] = [], uv: number[] = [], nor: number[] = [], idx: number[] = [];
  const blades = 9, segments = 4;
  for (let b = 0; b < blades; b++) {
    const a = n.hash(b, 1) * Math.PI * 2, r = n.hash(b, 2) * .2, h = .65 + n.hash(b, 3) * .45, lean = .18 + n.hash(b, 4) * .35, width = .03 + n.hash(b, 5) * .022;
    const ox = Math.cos(a) * r, oz = Math.sin(a) * r, face = a + Math.PI / 2 + (n.hash(b, 6) - .5);
    const fx = Math.cos(face), fz = Math.sin(face), base = pos.length / 3;
    for (let s = 0; s <= segments; s++) {
      const t = s / segments, y = t * h, bend = lean * t * t, w = width * (1 - t * .85);
      const cx = ox + Math.cos(a) * bend, cz = oz + Math.sin(a) * bend;
      for (const side of [-1, 1]) {
        pos.push(cx + fx * w * side, y, cz + fz * w * side); uv.push(side * .5 + .5, t);
        const up = new T.Vector3(Math.cos(a) * .35, 1, Math.sin(a) * .35).normalize(); nor.push(up.x, up.y, up.z);
      }
      if (s < segments) { const k = base + s * 2; idx.push(k, k + 1, k + 2, k + 1, k + 3, k + 2); }
    }
  }
  const geo = new T.BufferGeometry();
  geo.setAttribute('position', new T.Float32BufferAttribute(pos, 3)); geo.setAttribute('uv', new T.Float32BufferAttribute(uv, 2)); geo.setAttribute('normal', new T.Float32BufferAttribute(nor, 3));
  geo.setIndex(idx); return geo;
}

/** Wind-animated grass tufts near the riding line, split into chunks so only nearby ones draw. */
export class Grass {
  group = new T.Group(); private chunks: { mesh: T.InstancedMesh; x: number; z: number }[] = [];
  private uniforms = { uTime: { value: 0 }, uFade: { value: 46 }, uTip: { value: new T.Color() } };
  constructor(track: RideTrack, terrain: Terrain, amount: number) {
    const style = STYLES[track.biome]; this.group.name = 'grass';
    if (amount <= 0) return;
    this.uniforms.uTip.value.set(style.tip);
    const geometry = tuftGeometry(track.seed), noise = makeNoise(track.seed + 99), rng = mulberry(track.seed * 7 + 3);
    const material = new T.MeshStandardMaterial({ color: '#ffffff', roughness: .82, side: T.DoubleSide });
    material.onBeforeCompile = shader => {
      Object.assign(shader.uniforms, this.uniforms);
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nuniform float uTime; uniform float uFade; varying float vGrassH;')
        .replace('#include <project_vertex>', `
          vec3 iOrigin = ( modelMatrix * instanceMatrix * vec4( 0.0, 0.0, 0.0, 1.0 ) ).xyz;
          float gd = distance( iOrigin.xz, cameraPosition.xz );
          transformed *= 1.0 - smoothstep( uFade * 0.55, uFade, gd );
          vec4 mvPosition = modelMatrix * instanceMatrix * vec4( transformed, 1.0 );
          float ph = uTime * 1.6 + iOrigin.x * 0.11 + iOrigin.z * 0.09;
          float gust = sin( ph ) * 0.6 + sin( ph * 2.7 + 1.3 ) * 0.25 + sin( uTime * 0.37 + iOrigin.x * 0.02 ) * 0.5;
          float k = uv.y * uv.y;
          mvPosition.x += gust * k * 0.16; mvPosition.z += cos( ph * 0.83 ) * k * 0.07;
          vGrassH = uv.y;
          mvPosition = viewMatrix * mvPosition; gl_Position = projectionMatrix * mvPosition;`);
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', '#include <common>\nuniform vec3 uTip; varying float vGrassH;')
        .replace('#include <color_fragment>', '#include <color_fragment>\n diffuseColor.rgb = mix( diffuseColor.rgb * 0.42, mix( diffuseColor.rgb, uTip, 0.35 ), smoothstep( 0.0, 1.0, vGrassH ) );');
    };
    material.customProgramCacheKey = () => 'tq-grass';
    const hw = track.width / 2, chunk = 36, colors = style.colors.map(c => new T.Color(c)), dummy = new T.Object3D(), field = terrain.field;
    for (let d0 = 0; d0 < track.length; d0 += chunk) {
      const area = chunk * 2 * 22, count = Math.floor(area * style.density * amount);
      const mesh = new T.InstancedMesh(geometry, material, count); let n = 0;
      for (let i = 0; i < count; i++) {
        const d = d0 + rng() * chunk, side = rng() < .5 ? -1 : 1, l = side * (hw + 1 + rng() ** 1.35 * 21);
        const c = field.center(d), x = c.x - c.tz * l, z = c.z + c.tx * l;
        const patch = noise.fbm(x / 9, z / 9, 3);
        if (patch < .38 + (Math.abs(l) < hw + 2.5 ? .2 : 0) || rng() > .35 + patch) continue;
        const y = terrain.heightAt(x, z);
        if (terrain.waterLevel !== null && y < terrain.waterLevel + .3) continue;
        const h = style.height[0] + (style.height[1] - style.height[0]) * (rng() * .6 + patch * .4);
        dummy.position.set(x, y - .02, z); dummy.rotation.set((rng() - .5) * .2, rng() * 6.28, (rng() - .5) * .2); dummy.scale.set(.8 + rng() * .6, h, .8 + rng() * .6); dummy.updateMatrix();
        mesh.setMatrixAt(n, dummy.matrix); mesh.setColorAt(n, colors[Math.floor(rng() * colors.length)].clone().multiplyScalar(.85 + rng() * .3)); n++;
      }
      mesh.count = n; if (!n) { mesh.dispose(); continue; }
      mesh.instanceMatrix.needsUpdate = true; if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      mesh.computeBoundingSphere(); mesh.receiveShadow = true; mesh.frustumCulled = true;
      const c = field.center(d0 + chunk / 2); this.chunks.push({ mesh, x: c.x, z: c.z }); this.group.add(mesh);
    }
  }
  update(time: number, camera: T.Vector3) {
    this.uniforms.uTime.value = time;
    for (const c of this.chunks) c.mesh.visible = Math.hypot(c.x - camera.x, c.z - camera.z) < 80;
  }
  dispose() { const g = this.chunks[0]?.mesh.geometry; this.chunks.forEach(c => { (c.mesh.material as T.Material).dispose(); c.mesh.dispose(); }); g?.dispose(); }
}

export function mulberry(seed: number) { return () => { seed |= 0; seed = seed + 0x6D2B79F5 | 0; let t = Math.imul(seed ^ seed >>> 15, 1 | seed); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; }; }
